import type { MissingSectionFilesResponse } from "../../../lib/corpusApi";
import type { CorpusScanBadge } from "./corpus_scan_contract";

/** The result of a section-file scan as its card shows it. */
export interface MissingSectionFilesScanView {
  checked: string;
  missingJudge: string;
  missingExcluded: string;
  /** The documents missing at least one file, "12 documents". */
  missingAny: string;
  /** What the launch button says; null when no document is missing a file. */
  launchLabel: string | null;
}

/** What a section-file scan found, in a word for its card. */
export function describeMissingSectionFilesBadge({
  missingAny,
}: MissingSectionFilesResponse): CorpusScanBadge {
  return missingAny > 0
    ? { label: `${missingAny} missing`, tone: "attention" }
    : { label: "All present", tone: "ok" };
}

/** The wording of the confirmation dialog shown before a document sections refresh starts. */
export interface DocumentSectionsRefreshConfirmation {
  title: string;
  message: string;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Turns a section-file scan into the words of its card. */
export function describeMissingSectionFilesScan(
  counts: MissingSectionFilesResponse
): MissingSectionFilesScanView {
  const missingAny = pluralize(counts.missingAny, "document");
  return {
    checked: pluralize(counts.checked, "document"),
    missingJudge: pluralize(counts.missingJudge, "document"),
    missingExcluded: pluralize(counts.missingExcluded, "document"),
    missingAny,
    launchLabel: counts.missingAny > 0 ? `Refresh sections of ${missingAny}` : null,
  };
}

/** The wording of the confirmation before a document sections refresh starts. */
export function describeDocumentSectionsRefreshConfirmation({
  missingAny,
}: MissingSectionFilesResponse): DocumentSectionsRefreshConfirmation {
  return {
    title: "Refresh the sections of these documents?",
    message:
      `Starts a batch job that fetches the ${pluralize(missingAny, "document")} missing a section ` +
      `file again, rewrites their section files and embeds them again. Scorecards and annotations ` +
      `are not touched. It runs at a throttled pace and spends extraction and embedding budget.`,
  };
}
